import { useSelector } from 'react-redux';
import tenantTheme from '@theme';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'clsx';
import { useApplyProductModalDataOman } from '../../../../../hooks/useApplyProductModalDataOman';
import { Flex, Button, Icon } from '../../../../../components/common';
import { useApplyProductModalDataEg } from '../../../../../hooks/useApplyProductModalDataEg';
import { TENANT_KEY } from '../../../../../utility/env';
import { UnifiedListingAppliedBadgeIcon } from '../../../../../components/svg';
import { QuotaCreditModal } from './quotaCreditModal';
import { listingActionClickEvent } from '../../../../../services/analyticsService';
import {
  getUnifiedListingActionLabelKey,
  getUnifiedListingActionTitleKey,
} from '../../../utils/unifiedListingProductCopy';
import {
  getUnifiedListingActionConfig,
  getUnifiedListingActionVisuals,
} from '../../../utils/unifiedListingActionStyles';
import { UnifiedListingActionButton } from './styled';

const getPlatformActionButtonTitle = (action, useUnifiedListingLabels) => {
  const slug = action?.slug;
  if (useUnifiedListingLabels && slug) {
    const labelKey = getUnifiedListingActionLabelKey(slug);
    if (labelKey) return labelKey;
  }
  return action?.title ?? '';
};

const partitionUnifiedListingActions = (actions) => {
  if (!actions?.length) return [];
  const bySlug = (slug) => actions.find((a) => a?.slug === slug);
  return [
    bySlug('signature-listing') || bySlug('elite-listing'),
    bySlug('hot-listing') || bySlug('feature'),
    bySlug('refresh') || bySlug('boost-to-top'),
  ].filter(Boolean);
};

const getUnifiedActionSizeStyles = (config, isApplied) => {
  const size = config?.size;
  if (!size) return {};
  const width = isApplied && config?.appliedLabelKey ? 'auto' : size.width;
  return {
    width,
    minWidth: size.width,
    height: size.height,
    minHeight: size.height,
    maxHeight: size.height,
    paddingInline: config?.iconOnlyApplicable ? 0 : isApplied ? 8 : 10,
    paddingBlock: 0,
    fontSize: size.fontSize,
    lineHeight: `${size.height}px`,
    '--btn-padding-y': 0,
    flexShrink: 0,
  };
};

const PlatformActions = (props) => {
  const { t } = useTranslation();
  const { data = [], stories = {}, location, purpose, stats, unifiedMobileInlineRow, ...rest } = props;
  const isMobile = useSelector((state) => state.app.AppConfig.isMobile);

  const deductionModalRef = useRef();
  const { user } = useSelector((state) => state.app.loginUser);
  const isMultiPlatform = user?.isMultiPlatform;

  const serviceDataRef = useRef();

  const omanApplyProductData = useApplyProductModalDataOman(user, deductionModalRef, serviceDataRef);
  const egApplyProductData = useApplyProductModalDataEg(user, deductionModalRef, serviceDataRef);
  const {
    confirmationLoading,
    selectedPaymentOption,
    paymentOptions,
    action,
    onSuccessQuotaCreditModal,
    getDeduction,
    onChangePaymentOption,
    onCancelModal,
    actionLoading,
  } = TENANT_KEY === 'eg' ? egApplyProductData : useApplyProductModalDataOman(
    user,
    deductionModalRef,
    serviceDataRef,
    rest?.unifiedListingActionLabels ?? rest?.isUnifiedListings,
  );

  const inlineRow = unifiedMobileInlineRow && isMobile;
  const useUnifiedListingActionLabels = rest?.unifiedListingActionLabels ?? rest?.isUnifiedListings;
  const platformsForActionButtons =
    useUnifiedListingActionLabels && rest?.unified ? [{ slug: 'unified' }] : user?.platforms || [];
  const unifiedModalTitleKey =
    useUnifiedListingActionLabels && action?.applicableProduct
      ? getUnifiedListingActionTitleKey(action.applicableProduct)
      : null;

  const unifiedLayout = useUnifiedListingActionLabels && rest?.unified;

  const renderUnifiedActionContent = (actionItem, config, visuals, isApplied) => {
    const labelKey = isApplied && config?.appliedLabelKey ? config.appliedLabelKey : config?.labelKey;
    const contentColor = visuals?.contentColor ?? visuals?.color;

    if (isApplied) {
      return (
        <Flex align="center" justify="center" gap="4px" style={{ width: '100%', whiteSpace: 'nowrap' }}>
          <Icon icon="HiCheck" size="12px" color={visuals?.tickColor ?? contentColor} />
          {config?.iconOnlyApplicable ? (
            <Icon icon={config.actionIcon} size="17px" color={contentColor} />
          ) : (
            <span>{t(labelKey)}</span>
          )}
        </Flex>
      );
    }

    if (config?.iconOnlyApplicable && config?.actionIcon) {
      return <Icon icon={config.actionIcon} size="17px" color={contentColor} />;
    }

    return t(labelKey ?? getPlatformActionButtonTitle(actionItem, useUnifiedListingActionLabels));
  };

  const renderOmanPlatformActionButton = (e, platform, index, wrapperProps = {}) => {
    const isApplied = !!e?.applied;
    const disabled = isApplied || !e?.canApply;
    const actionConfig = useUnifiedListingActionLabels ? getUnifiedListingActionConfig(e?.slug) : null;
    const actionVisuals = actionConfig ? getUnifiedListingActionVisuals(e?.slug, isApplied) : null;
    const isUnifiedAction = !!(rest?.isUnifiedListings && actionConfig && actionVisuals);
    const { className: wrapClassName, style: wrapStyle } = wrapperProps;

    const buttonStyles = {
      gap: '4px',
      backgroundBlendMode: isUnifiedAction ? 'normal' : undefined,
      backgroundRepeat: isUnifiedAction ? 'no-repeat' : undefined,
      backgroundSize: isUnifiedAction ? 'cover' : undefined,
      ...(isUnifiedAction && actionConfig ? getUnifiedActionSizeStyles(actionConfig, isApplied) : {}),
      ...(!isUnifiedAction && {
        borderColor: tenantTheme['gray-100'],
        paddingInline: rest?.isUnifiedListings ? 18 : 12,
        paddingBlock: rest?.isUnifiedListings ? 11 : undefined,
        minHeight: rest?.isUnifiedListings ? 40 : undefined,
        minWidth: unifiedLayout || inlineRow ? undefined : !isMobile || (!isMultiPlatform && '156px'),
        width: unifiedLayout ? '100%' : undefined,
      }),
      ...(isUnifiedAction
        ? {
            background: actionVisuals.background,
            backgroundImage: actionVisuals.backgroundImage ?? actionVisuals.background,
            backgroundColor: actionVisuals.backgroundImage ? undefined : actionVisuals.background,
            color: actionVisuals.contentColor,
            border: 'none',
            borderColor: 'transparent',
            '--unified-action-bg': actionVisuals.background,
            '--unified-action-bg-image': actionVisuals.backgroundImage ?? 'none',
            '--unified-action-border': 'transparent',
            '--unified-action-content-color': actionVisuals.contentColor,
            '--content-color': actionVisuals.contentColor,
            '--ant-button-default-hover-bg': isApplied ? actionVisuals.background : 'transparent',
          }
        : {
            '--ant-button-default-hover-bg': tenantTheme['primary-light-4'],
            '--content-color': tenantTheme['primary-color'],
          }),
    };

    const actionButtonClassName = cx(
      !isUnifiedAction && !inlineRow && 'w-100',
      isUnifiedAction && (isApplied ? 'unified-listing-action-applied' : 'unified-listing-action-applicable'),
    );

    const handleClick = () => {
      listingActionClickEvent(user, e?.appliedTitle, purpose);
      getDeduction('credit', props, e, e?.platform);
    };

    const buttonElement = isUnifiedAction ? (
      <UnifiedListingActionButton
        className={actionButtonClassName}
        onClick={handleClick}
        style={buttonStyles}
        disabled={disabled}
        loading={false}
      >
        {renderUnifiedActionContent(e, actionConfig, actionVisuals, isApplied)}
      </UnifiedListingActionButton>
    ) : (
      <Button
        className={actionButtonClassName}
        onClick={handleClick}
        style={buttonStyles}
        disabled={disabled}
        loading={useUnifiedListingActionLabels ? false : actionLoading?.[`${props?.slug}${e?.name}`]}
        type={e?.platform === 'dubizzle' ? 'dubizzlePrimary' : undefined}
        icon={rest?.isUnifiedListings ? undefined : e?.platformIcon}
        iconSize={rest?.isUnifiedListings ? undefined : '20px'}
      >
        {t(getPlatformActionButtonTitle(e, useUnifiedListingActionLabels))}
      </Button>
    );

    return (
      <div
        key={`${platform.slug}-${index}`}
        className={wrapClassName ?? (inlineRow || isUnifiedAction ? '' : 'w-100')}
        style={{
          ...(inlineRow || isUnifiedAction ? { flexShrink: 0 } : {}),
          ...(inlineRow ? { flex: '1 1 auto' } : {}),
          ...wrapStyle,
        }}
      >
        {buttonElement}
      </div>
    );
  };

  return (
    <Flex
      vertical={!inlineRow && !isMobile && isMultiPlatform}
      gap="8px"
      className={inlineRow ? '' : 'w-100'}
      wrap={inlineRow}
      align="center"
      style={{
        ...(inlineRow ? { width: 'auto', maxWidth: '100%', flex: '1 1 auto', minWidth: 0 } : {}),
        ...rest.style,
      }}
    >
      <QuotaCreditModal
        ref={deductionModalRef}
        title={unifiedModalTitleKey ? t(unifiedModalTitleKey) : action?.applicableProduct?.requestTitle}
        action={action}
        okText={
          useUnifiedListingActionLabels ? 'Apply Now' : action?.isSufficient ? 'Submit' : 'Continue'
        }
        onSuccess={onSuccessQuotaCreditModal}
        onCancel={onCancelModal}
        loading={confirmationLoading}
        serviceDataRef={serviceDataRef}
        selectedPaymentOption={selectedPaymentOption}
        paymentOptions={paymentOptions}
        onChangePaymentOption={onChangePaymentOption}
        showInsufficientCreditsAlert
        showInfoMessage
        hideDataTable
        unifiedCreditsOnly={useUnifiedListingActionLabels}
      />
      <>
        {platformsForActionButtons?.map((platform) => {
          const platformActions = rest?.[platform.slug]?.listingPlatformActions;
          if (!platformActions?.length) return null;

          if (unifiedLayout && platform.slug === 'unified') {
            const unifiedActions = partitionUnifiedListingActions(platformActions);
            return (
              <Flex
                key={`${platform.slug}-unified-row`}
                gap="6px"
                wrap={false}
                align="center"
                className="w-100"
                style={{ minWidth: 0 }}
              >
                {unifiedActions.map((actionItem, i) =>
                  renderOmanPlatformActionButton(actionItem, platform, i),
                )}
              </Flex>
            );
          }

          return platformActions.map((actionItem, actionIndex) =>
            renderOmanPlatformActionButton(actionItem, platform, actionIndex),
          );
        })}
      </>
    </Flex>
  );
};

export default PlatformActions;
